import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DEFAULT_ENROLLMENT_FEE_CENTS, PaymentConcept } from '@academia-apolo/shared-types';
import { Input } from '@/shared/components/Input';
import { Select } from '@/shared/components/Select';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/lib/api-client';
import { useRegisterPayment } from '../hooks/usePayments';
import { MONTH_OPTIONS, todayISODate } from '@/shared/utils/format';
import { useStudents } from '@/features/director-students/hooks/useStudents';
import { StudentSearchSelect } from '@/features/director-students/components/StudentSearchSelect';
const STATUS_OPTIONS = [
    { value: 'paid', label: 'Pagado' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'overdue', label: 'Vencido' },
    { value: 'exempt', label: 'Exento' },
];
export function RegisterPaymentForm({ onSuccess, concept = 'monthly', lockedStudentId }) {
    const [serverError, setServerError] = useState(null);
    const registerPayment = useRegisterPayment();
    const { data: students = [] } = useStudents({ status: 'active' });
    const isEnrollment = concept === 'enrollment';
    const now = new Date();
    const { register, handleSubmit, watch, setValue, formState: { errors }, } = useForm({
        defaultValues: {
            student_id: lockedStudentId ?? '',
            month: String(now.getMonth() + 1),
            year: String(now.getFullYear()),
            // Matrícula is normally generated as a charge to be collected later.
            status: isEnrollment ? 'pending' : 'paid',
            paid_date: todayISODate(),
            ...(isEnrollment && { amount: (DEFAULT_ENROLLMENT_FEE_CENTS / 100).toFixed(2) }),
        },
    });
    const selectedStudentId = watch('student_id');
    const selectedStudent = students.find((s) => s.id === selectedStudentId);
    // Monthly: prefill the selected student's fee (110€ invitation / 150€ standard).
    // Enrollment: prefill the standard matrícula fee. Both stay editable.
    useEffect(() => {
        if (isEnrollment)
            return; // enrollment amount is set once via defaultValues
        if (selectedStudent) {
            setValue('amount', (selectedStudent.monthly_fee_cents / 100).toFixed(2));
        }
    }, [selectedStudent, setValue, isEnrollment]);
    function onSubmit(values) {
        setServerError(null);
        const amountCents = Math.round(parseFloat(values.amount) * 100);
        if (isNaN(amountCents) || amountCents <= 0) {
            setServerError('Introduce un importe válido');
            return;
        }
        registerPayment.mutate({
            student_id: values.student_id,
            month: parseInt(values.month, 10),
            year: parseInt(values.year, 10),
            amount: amountCents,
            status: values.status,
            concept: isEnrollment ? PaymentConcept.ENROLLMENT : PaymentConcept.MONTHLY,
            ...(values.status === 'paid' && values.paid_date && { paid_at: values.paid_date }),
            ...(values.notes && { notes: values.notes }),
        }, {
            onSuccess: () => onSuccess(),
            onError: (err) => {
                setServerError(err instanceof ApiError ? err.message : 'Ha ocurrido un error.');
            },
        });
    }
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-4", noValidate: true, children: [isEnrollment && (_jsx("p", { className: "rounded-lg bg-brand/5 px-3 py-2 text-xs text-gray-600 dark:bg-brand/10 dark:text-gray-300", children: "La matr\u00EDcula se registra como un pago aparte de la mensualidad. El importe est\u00E1ndar viene prerrellenado; puedes ajustarlo si hay alg\u00FAn caso especial." })), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("input", { type: "hidden", ...register('student_id', { required: 'Selecciona un alumno' }) }), !lockedStudentId && (_jsxs(_Fragment, { children: [_jsx("label", { className: "text-sm font-medium text-gray-700 dark:text-gray-300", children: "Alumno" }), _jsx(StudentSearchSelect, { students: students, value: selectedStudentId || null, onChange: (id) => setValue('student_id', id ?? '', { shouldValidate: true }) }), errors.student_id?.message && (_jsx("p", { className: "text-xs text-red-600 dark:text-red-400", children: errors.student_id.message }))] }))] }), _jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Select, { label: "Mes", options: MONTH_OPTIONS, error: errors.month?.message, ...register('month', { required: true }) }), _jsx(Input, { label: "A\u00F1o", type: "number", error: errors.year?.message, ...register('year', { required: true }) })] }), _jsx(Input, { label: "Importe (\u20AC)", type: "number", step: "0.01", hint: "Introduce el importe en euros", error: errors.amount?.message, ...register('amount', { required: 'El importe es obligatorio' }) }), _jsx(Select, { label: "Estado", options: STATUS_OPTIONS, error: errors.status?.message, ...register('status', { required: true }) }), watch('status') === 'paid' && (_jsx(Input, { label: "Fecha de cobro", type: "date", max: todayISODate(), hint: "Hoy por defecto; puedes elegir un d\u00EDa anterior si el cobro fue antes.", error: errors.paid_date?.message, ...register('paid_date', {
                    required: 'La fecha de cobro es obligatoria',
                    validate: (v) => v <= todayISODate() || 'La fecha no puede ser futura',
                }) })), _jsx(Input, { label: "Notas (opcional)", ...register('notes') }), serverError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: serverError })), _jsx(Button, { type: "submit", loading: registerPayment.isPending, className: "w-full", children: isEnrollment ? 'Generar matrícula' : 'Registrar pago' })] }));
}
//# sourceMappingURL=RegisterPaymentForm.js.map